// Import Pinia functions
import { defineStore } from "pinia";
import { ref, computed } from "vue";
import api from "@/services/api";
// import { AxiosHeaders } from 'axios';

/**
 * Auth Store - Manages user authentication state
 *
 * This store handles:
 * - Login/logout
 * - Storing JWT token
 * - Storing user info
 * - Checking if user is authenticated
 */
export const useAuthStore = defineStore("auth", () => {
    // ==================== STATE (Data) ====================

    const token = ref(localStorage.getItem("token") || null);

    // Initialize user from localStorage
    const getUserFromStorage = () => {
        const storedUser = localStorage.getItem("user");
        if (storedUser) {
            try {
                return JSON.parse(storedUser);
            } catch (error) {
                console.error("Failed to parse stored user:", error);
                return null;
            }
        }
        return null;
    };

    const user = ref(getUserFromStorage());

    // Settings state (separate from user profile)
    const settings = ref(null);

    // Payment loading state
    const checkoutLoading = ref(false);

    // ==================== GETTERS (Computed values) ====================

    // Tier levels for comparison (higher = more access)
    const TIER_LEVELS = {
        "FREE": 0,
        "TIER 1": 1,
        "TIER 2": 2,
        "TIER 3": 3,
        "ADMIN": 4
    };

    // computed() creates a value that updates automatically
    const isAuthenticated = computed(() => !!token.value);
    const userTier = computed(() => user.value?.subscription_tier || "FREE");
    const isAdmin = computed(() => user.value?.is_admin);

    /**
     * Check if user has at least a specific tier level
     * @param {string} minTier - Minimum tier required ("FREE", "TIER 1", "TIER 2", "TIER 3", "ADMIN")
     * @returns {boolean} True if user's tier >= minTier
     */
    function hasTierAccess(minTier) {
        const currentLevel = TIER_LEVELS[userTier.value] ?? 0;
        const requiredLevel = TIER_LEVELS[minTier] ?? 0;
        return currentLevel >= requiredLevel;
    }

    // ==================== ACTIONS (Functions) ====================

    /**
     * Login user
     * @param {string} email
     * @param {string} password
     * @returns {Promise<Object>} User data
     */
    async function login(email, password) {
        try {
            // Call API
            const response = await api.login(email, password);

            // Extract token from response
            const accessToken = response.data.access_token;

            // Save to store
            token.value = accessToken;

            // Save to localStorage (persists across page refreshes)
            localStorage.setItem("token", accessToken);

            localStorage.setItem("refreshToken", response.data.refresh_token);

            const me = await api.getCurrentUser();
            const userData = me.data;
            user.value = userData;
            localStorage.setItem("user", JSON.stringify(userData));

            // Fetch settings separately
            await fetchSettings();

            return userData;
        } catch (error) {
            console.error(error);
            if (error.response?.status === 429) {
                throw new Error("Too many login attempts. Please slow down and try again later.");
            }
            const message = error.response?.data?.detail || "Login failed";
            throw new Error(message);
        }
    }

    /**
     * Register new user
     * @param {string} fullName - User's full name
     * @param {string} email - User's email
     * @param {string} password - User's password
     * @param {string} school - User's school
     * @returns {Promise<Object>} User data
     */
    async function register(fullName, email, password, school) {
        try {
            await api.register({
                full_name: fullName,
                email: email,
                password: password,
                school: school
            });

            // After registration, automatically log in
            await login(email, password);

            return user.value;
        } catch (error) {
            if (error.response?.status === 429) {
                throw new Error("Too many registration attempts. Please slow down and try again later.");
            }
            let message = "Registration failed";
            const detail = error.response?.data?.detail;
            if (typeof detail === "string") {
                message = detail;
            } else if (Array.isArray(detail) && detail.length > 0) {
                // FastAPI validation errors return an array of objects
                message = detail[0].msg || message;
            }
            throw new Error(message);
        }
    }

    /**
     * Update user profile
     * @param {Object} profileData - Profile data to update
     * @param {string} profileData.full_name - New full name (optional)
     * @param {string} profileData.email - New email (optional)
     * @param {string} profileData.school - New school (optional)
     * @returns {Promise<Object>} Updated user data
     */
    async function updateProfile(profileData) {
        try {
            // Filter out undefined/null values
            const dataToSend = {};
            if (
                profileData.full_name !== undefined &&
                profileData.full_name !== null
            ) {
                dataToSend.full_name = profileData.full_name;
            }
            if (profileData.email !== undefined && profileData.email !== null) {
                dataToSend.email = profileData.email;
            }
            if (
                profileData.school !== undefined &&
                profileData.school !== null
            ) {
                dataToSend.school = profileData.school;
            }

            const response = await api.updateProfile(dataToSend);
            const updatedUser = response.data;

            // Update store and localStorage
            user.value = updatedUser;
            localStorage.setItem("user", JSON.stringify(updatedUser));

            return updatedUser;
        } catch (error) {
            const message =
                error.response?.data?.detail || "Profile update failed";
            throw new Error(message);
        }
    }

    /**
     * Change user password
     * @param {string} currentPassword - Current password
     * @param {string} newPassword - New password
     * @returns {Promise<Object>} Success message
     */
    async function changePassword(currentPassword, newPassword) {
        try {
            const response = await api.changePassword({
                current_password: currentPassword,
                new_password: newPassword,
            });

            return response.data;
        } catch (error) {
            const message =
                error.response?.data?.detail || "Password change failed";
            throw new Error(message);
        }
    }

    /**
     * Logout user
     */
    function logout() {
        // Clear store
        token.value = null;
        user.value = null;
        settings.value = null;

        // Clear localStorage
        localStorage.removeItem("token");
        localStorage.removeItem("user");
        localStorage.removeItem("refreshToken");
    }

    /**
     * Initialize store on app load
     * Checks if token exists and loads user data
     */
    async function initialize() {
        const storedToken = localStorage.getItem("token");
        const refreshToken = localStorage.getItem("refreshToken");

        // If we have a refresh token but no access token, try to refresh
        if (!storedToken && refreshToken) {
            try {
                const { data } = await api.refresh({
                    refresh_token: refreshToken,
                });

                token.value = data.token;
                localStorage.setItem("token", data.token);
            } catch (error) {
                console.error("Failed to refresh token on init:", error);
                logout(); // Clear everything if refresh fails
                return;
            }
            window.location.reload();
        }

        if (token.value) {
            // Load user from localStorage
            const storedUser = localStorage.getItem("user");
            if (storedUser) {
                user.value = JSON.parse(storedUser);
            }

            // Fetch fresh settings from backend
            try {
                await fetchSettings();
            } catch (error) {
                console.error("Failed to fetch settings on init:", error);
            }
        }
    }

    async function fetchUser() {
        try {
            const response = await api.getCurrentUser();
            const userData = response.data;
            user.value = userData;
            localStorage.setItem("user", JSON.stringify(userData));
            return userData;
        } catch (error) {
            console.error("Failed to fetch user:", error);
            // If unauthorized, logout
            if (error.response?.status === 401) {
                logout();
            }
            throw error;
        }
    }

    /**
     * Fetch user settings (separate from profile)
     * Call this when you need to load/refresh settings
     */
    async function fetchSettings() {
        try {
            const response = await api.getSettings();
            settings.value = response.data;
            return settings.value;
        } catch (error) {
            console.error("Failed to fetch settings:", error);
            throw error;
        }
    }

    /**
     * Update user settings
     * @param {Object} updates - Partial settings updates
     */
    async function updateSettings(updates) {
        try {
            const response = await api.updateSettings(updates);
            settings.value = response.data;
            return settings.value;
        } catch (error) {
            console.error("Failed to update settings:", error);
            throw error;
        }
    }

    /**
     * Reset all settings to defaults
     */
    async function resetSettings() {
        try {
            const response = await api.resetSettings();
            settings.value = response.data;
            return settings.value;
        } catch (error) {
            console.error("Failed to reset settings:", error);
            throw error;
        }
    }

    // ==================== PAYMENT ACTIONS ====================

    /**
     * Initiate Stripe Checkout for tier upgrade
     * Redirects user to Stripe Checkout page
     * @param {string} tier - Target tier ("TIER 1" or "TIER 2")
     * @param {string} billingCycle - Billing cycle ("monthly" or "annual")
     */
    async function initiateCheckout(tier, billingCycle = 'monthly') {
        checkoutLoading.value = true;
        try {
            const response = await api.createCheckoutSession(tier, billingCycle);
            // Redirect to Stripe Checkout
            window.location.href = response.data.checkout_url;
        } catch (error) {
            checkoutLoading.value = false;
            const message = error.response?.data?.detail || 'Failed to start checkout';
            throw new Error(message);
        }
    }

    /**
     * Open Stripe Customer Portal for subscription management
     * Allows users to update payment method, cancel, change plan, view invoices
     */
    async function openCustomerPortal() {
        try {
            const response = await api.createPortalSession();
            // Redirect to Stripe Customer Portal
            window.location.href = response.data.portal_url;
        } catch (error) {
            const message = error.response?.data?.detail || 'Failed to open billing portal';
            throw new Error(message);
        }
    }

    /**
     * Get the numeric level of a tier for comparison
     * @param {string} tier - Tier name ("FREE", "TIER 1", etc.)
     * @returns {number} Tier level (0-4)
     */
    function getTierLevel(tier) {
        return TIER_LEVELS[tier] ?? 0;
    }

    // Return everything that other components can access
    return {
        // State
        token,
        user,
        settings,
        checkoutLoading,

        // Getters
        isAuthenticated,
        userTier,
        isAdmin,

        // Actions
        login,
        register,
        logout,
        updateProfile,
        changePassword,
        initialize,
        fetchUser,
        fetchSettings,
        updateSettings,
        resetSettings,
        hasTierAccess,
        getTierLevel,

        // Payment actions
        initiateCheckout,
        openCustomerPortal,
    };
});
